import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { OrderDto } from '@ecom/contracts';
import { useOrdersStore } from '@/stores/orders.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { errorMessage } from '@/lib/api-client';
import { count, date, dayRange } from '@/lib/format';
import { Alert, Button } from '@/components/ui';
import { itemCount } from './item-count';

/**
 * Packing slips for one order or a selection (ADR-059): what to put in the
 * box, and where it goes — never a price. The invoice is the tax document;
 * this is the sheet the person packing works from, one order to a page.
 *
 * Opened outside the shell so the printout is the slips and nothing else,
 * and printed as soon as they are on screen.
 */
export function PackingSlipsPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fetchMany = useOrdersStore((state) => state.fetchMany);

  const [orders, setOrders] = useState<OrderDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const printed = useRef(false);

  const ids = (params.get('ids') ?? '').split(',').filter(Boolean);
  const key = ids.join(',');

  useEffect(() => {
    let live = true;
    fetchMany(key.split(',').filter(Boolean))
      .then((loaded) => live && setOrders(loaded))
      .catch((reason: unknown) => live && setError(errorMessage(reason, 'Could not load these orders.')));
    return () => {
      live = false;
    };
  }, [fetchMany, key]);

  useEffect(() => {
    if (orders && orders.length > 0 && !printed.current) {
      printed.current = true;
      // A frame later, so the slips are laid out before the dialog snapshots them.
      requestAnimationFrame(() => window.print());
    }
  }, [orders]);

  return (
    <div className="min-h-screen bg-surface-sunken print:bg-white">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-line bg-surface px-6 py-3 print:hidden">
        <p className="text-sm font-medium text-ink">
          {t('packingSlips')} · {count(orders?.length ?? ids.length, locale)}
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate(-1)}>
            {t('back')}
          </Button>
          <Button onClick={() => window.print()} disabled={!orders}>
            {t('print')}
          </Button>
        </div>
      </div>

      {error ? (
        <div className="p-6">
          <Alert>{error}</Alert>
        </div>
      ) : null}
      {!orders && !error ? <p className="p-6 text-sm text-ink-muted">{t('loading')}</p> : null}

      <div className="mx-auto flex max-w-3xl flex-col gap-6 p-6 print:max-w-none print:gap-0 print:p-0">
        {orders?.map((order) => <Slip key={order.id} order={order} locale={locale} />)}
      </div>
    </div>
  );
}

function Slip({ order, locale }: { order: OrderDto; locale: 'ar' | 'en' }) {
  const t = useT();
  const address = order.shippingAddress;
  const units = order.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <article className="break-after-page rounded-card border border-line bg-white p-8 text-black print:rounded-none print:border-0">
      <header className="flex items-start justify-between gap-6 border-b border-black/15 pb-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-black/60">{t('packingSlip')}</p>
          <h1 dir="ltr" className="text-2xl font-bold">
            {order.number}
          </h1>
          <p className="text-sm text-black/70">{date(order.placedAt ?? order.createdAt, locale)}</p>
        </div>
        <div className="text-end text-sm">
          {order.shippingMethodName ? <p className="font-semibold">{order.shippingMethodName}</p> : null}
          {order.isPickup ? <p>{t('pickupAtStore')}</p> : null}
          {order.deliveryEstimate ? (
            <p className="text-black/70">{dayRange(order.deliveryEstimate.from, order.deliveryEstimate.to, locale)}</p>
          ) : null}
          <p className="mt-1 text-black/70">{order.paymentMethod === 'COD' ? t('paymentCod') : t('paymentCard')}</p>
        </div>
      </header>

      <section className="grid gap-6 border-b border-black/15 py-4 sm:grid-cols-2">
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-black/60">{t('packingSlipShipTo')}</h2>
          <address className="text-sm not-italic leading-relaxed">
            <strong>{address.fullName}</strong>
            <br />
            {[address.line1, address.line2, address.district].filter(Boolean).join(locale === 'ar' ? '، ' : ', ')}
            <br />
            {address.city}, {address.region}
            {address.shortCode ? (
              <>
                {' · '}
                <span dir="ltr">{address.shortCode}</span>
              </>
            ) : null}
            <br />
            <span dir="ltr">{address.phone}</span>
            {address.directions ? (
              <>
                <br />
                {address.directions}
              </>
            ) : null}
          </address>
        </div>
        {order.customerNote ? (
          <div>
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-black/60">{t('customerNote')}</h2>
            <p className="whitespace-pre-line text-sm">{order.customerNote}</p>
          </div>
        ) : null}
      </section>

      <table className="mt-4 w-full text-sm">
        <thead>
          <tr className="border-b border-black/15 text-start text-xs uppercase tracking-wide text-black/60">
            <th className="py-2 text-start font-semibold">{t('items')}</th>
            <th className="py-2 text-start font-semibold">{t('sku')}</th>
            <th className="py-2 text-end font-semibold">{t('packingSlipQty')}</th>
            <th className="w-16 py-2 text-end font-semibold">{t('packingSlipPacked')}</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((item) => (
            <tr key={item.id} className="border-b border-black/10 align-top">
              <td className="py-2.5 pe-4">
                <span className="block font-medium">{item.name}</span>
                {Object.keys(item.optionLabels).length > 0 ? (
                  <span className="block text-xs text-black/70">
                    {Object.entries(item.optionLabels)
                      .map(([name, value]) => `${name}: ${value}`)
                      .join(' · ')}
                  </span>
                ) : null}
                {item.fields.map((field) => (
                  <span key={field.code} className="block text-xs text-black/70">
                    {field.label}: <strong>{field.display}</strong>
                  </span>
                ))}
              </td>
              <td dir="ltr" className="py-2.5 pe-4 text-start font-mono text-xs">
                {item.sku}
              </td>
              <td className="py-2.5 text-end text-base font-semibold">{item.quantity}</td>
              <td className="py-2.5 text-end">
                <span aria-hidden className="inline-block size-4 border border-black/50" />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} className="pt-3 text-xs text-black/60">
              {itemCount(units, t, locale)}
            </td>
            <td />
            <td />
          </tr>
        </tfoot>
      </table>
    </article>
  );
}
